import React, {createContext, useContext, useRef} from 'react';
import useResizeObserver from 'use-resize-observer';

import {
  DEFAULT_LINE_PLOT_BREAKPOINTS,
  type PreparedBreakpoints,
} from '../../util/dynamicConfigs/linePlotPointDensity';
import {
  type PrimaryLineType,
  useFullFidelityModeLineType,
  useLinePlotPointDensityBreakpoints,
} from '../../util/rampFeatureFlags/linePlotDynamicConfigs';
import {
  useRampFlagDynamicFullScreenBinSizing,
  useRampFlagFullFidelityRunsPerChart,
} from '../../util/rampFeatureFlags/linePlotFeatureFlags';
import {type FullFidelityMode} from '../WorkspaceDrawer/Settings/types';
import {recordRumAction} from './../../util/datadog/datadogRum';
import {usePanelGroupingSettings} from './RunsLinePlotContext/usePanelGroupingSettings';

// uplot decimates line points once they exceed 4x the pixel width of the panel
// there's no point requesting more than this if we can't show them
// 10k points is the previous limit for full screen panels
export const FULL_SCREEN_BINS_PER_PIXEL = 4;
export const FULL_SCREEN_MAX_BINS = 10000;

// Bin-count policy for getPointsPerPanel.
// MIN_MAX_LAST_POINTS_PER_BIN: each min-max-last bin expands to up to three
// rendered points (xAtMin, xAtMax, xLast).
// MAX_RENDERED_POINTS_PER_WIDTH: uPlot begins decimating the array once points
// exceed 4x the panel pixel width; this is the rendering-layer invariant.
// MIN_MAX_LAST_BIN_RATIO: min-max-last targets 40% of the full-fidelity bin
// count, yielding ~1.2x payload with 3x edge-preserving density.
export const MIN_MAX_LAST_POINTS_PER_BIN = 3;
export const MAX_RENDERED_POINTS_PER_WIDTH = 4;
export const MIN_MAX_LAST_BIN_RATIO = 0.4;
export const FALLBACK_BIN_COUNT = 400;

// Tiered bin-count target as a function of panel pixel width. Shared by the
// full-fidelity return path (used directly) and the min-max-last path (scaled
// by MIN_MAX_LAST_BIN_RATIO).
function defaultBinCountForWidth(
  width: number,
  breakpoints: PreparedBreakpoints
): number {
  for (const [threshold, bins] of breakpoints) {
    if (width > threshold) {
      return bins;
    }
  }
  return FALLBACK_BIN_COUNT;
}

type PanelInteractionContextType = {
  fullScreenNumBuckets: number;
  numBucketsByPanelWidth: number | undefined;
  setZooming: (isZooming: boolean) => void;
  sizeRef: ReturnType<typeof useResizeObserver>['ref'];
  zooming: boolean;
};

export const PanelInteractionContext =
  createContext<PanelInteractionContextType | null>(null);

// Log how wide the panels are on when a panel loads so we can get a distribution of user panel sizes
export const useTrackPanelWidth = (width: number | undefined) => {
  const hasLogged = useRef(false);

  if (width && !hasLogged.current) {
    hasLogged.current = true;
    recordRumAction('workspace_component_measure', {runLinePlotWidth: width});
  }
};

export const PanelInteractionProvider = ({
  activeRunLimit,
  children,
  fullFidelityMode,
  groupedFullFidelityMode,
}: {
  activeRunLimit: number | undefined;
  children: React.ReactNode;
  fullFidelityMode: FullFidelityMode | undefined;
  groupedFullFidelityMode: FullFidelityMode | undefined;
}) => {
  const [zooming, setZooming] = React.useState(false);
  /**
   * We don't have any wrapping elements at this height in the React tree, so we're going to produce a ref to send downstream to the layout component. This will let us avoid creating an extra wrapping div.
   */
  const {ref, width} = useResizeObserver();
  useTrackPanelWidth(width);
  const canUseDynamicFullScreenBinSizing =
    useRampFlagDynamicFullScreenBinSizing();
  const canOverrideMaxRuns = useRampFlagFullFidelityRunsPerChart();
  const breakpoints = useLinePlotPointDensityBreakpoints();
  const {isAnyRunsetGrouped} = usePanelGroupingSettings();
  const primaryLineType = useFullFidelityModeLineType({
    fullFidelityMode,
    groupedFullFidelityMode,
    isGrouped: isAnyRunsetGrouped,
  });

  const fullScreenNumBuckets = getFullScreenNumBuckets({
    canUseDynamicFullScreenBinSizing,
    width,
  });

  const numBucketsByPanelWidth = getPointsPerPanel({
    breakpoints,
    activeRunLimit,
    canOverrideMaxRuns,
    primaryLineType,
    width,
  });

  const derivedPointsByPanelWidth = numBucketsByPanelWidth
    ? Math.floor(numBucketsByPanelWidth)
    : undefined;

  const value = React.useMemo(
    () => ({
      fullScreenNumBuckets,
      numBucketsByPanelWidth: derivedPointsByPanelWidth,
      setZooming: (isZooming: boolean) => {
        setZooming(isZooming);
      },
      sizeRef: ref,
      width,
      zooming,
    }),
    [
      fullScreenNumBuckets,
      derivedPointsByPanelWidth,
      setZooming,
      ref,
      width,
      zooming,
    ]
  );
  return (
    <PanelInteractionContext.Provider value={value}>
      {children}
    </PanelInteractionContext.Provider>
  );
};

export function getFullScreenNumBuckets({
  canUseDynamicFullScreenBinSizing,
  width,
}: {
  canUseDynamicFullScreenBinSizing: boolean;
  width: number | undefined;
}): number {
  if (canUseDynamicFullScreenBinSizing && width) {
    return Math.min(
      Math.floor(width * FULL_SCREEN_BINS_PER_PIXEL),
      FULL_SCREEN_MAX_BINS
    );
  }
  return FULL_SCREEN_MAX_BINS;
}

export function getPointsPerPanel({
  breakpoints = DEFAULT_LINE_PLOT_BREAKPOINTS,
  activeRunLimit,
  canOverrideMaxRuns,
  primaryLineType,
  width,
}: {
  breakpoints?: PreparedBreakpoints;
  activeRunLimit: number | undefined;
  canOverrideMaxRuns: boolean;
  primaryLineType?: PrimaryLineType;
  width: number | undefined;
}) {
  // make sure this is first because this needs to return undefined until we have a panel width
  if (width === undefined) {
    return undefined;
  }

  // Guard against non-finite or non-positive widths. Transient DOM states
  // (a zero width during layout, NaN from a detached node) must not propagate
  // into bin calculations — downstream consumers floor the result and treat
  // 0 as "panel not yet measured," which would permanently skip the query.
  if (!Number.isFinite(width) || width <= 0) {
    return undefined;
  }

  /**
   * We apply a compression factor when we're allowing more than 50 runs per chart with the ramp flag AND when a user has configured the max runs limit in excess of the standard limit for FF. As the user limit exceeds 50 by larger and larger amounts we want to progressively compress the number of points per panel to shave load off of the chart rendering process in React.
   */
  // 100 runs
  // progressiveFactor =(100 runs - 50)/ 50 => 1
  // factor = 1 - (.5 * 1) => 0.5
  //
  // 75 runs
  // progressiveFactor =(75 runs - 50)/ 50 => 0.5
  // factor = 1 - (.5 * 0.5) => 0.75
  //
  // 50 runs
  // progressiveFactor =(50 runs - 50)/ 50 => 0
  // factor = 1 - (.5 * 0) => 1
  const applyCompressionFactor =
    activeRunLimit && activeRunLimit > 50 && canOverrideMaxRuns;
  // don't let this number exceed 1 or fall below 0
  const progressiveFactor = Math.max(
    0,

    Math.min(1, ((activeRunLimit ?? 50) - 50) / 50)
  );

  const factor = !applyCompressionFactor ? 1 : 1 - 0.5 * progressiveFactor;

  const defaultBinCount = defaultBinCountForWidth(width, breakpoints);

  if (primaryLineType === 'min-max-last') {
    // MinMaxLast mode: each bin can expand to up to
    // MIN_MAX_LAST_POINTS_PER_BIN rendered points (xAtMin, xAtMax, xLast).
    // Target MIN_MAX_LAST_BIN_RATIO of the full-fidelity bin count, capped so
    // that bins * MIN_MAX_LAST_POINTS_PER_BIN never exceeds
    // MAX_RENDERED_POINTS_PER_WIDTH * width (the uPlot decimation threshold).
    const minMaxLastBinTarget = Math.floor(
      defaultBinCount * MIN_MAX_LAST_BIN_RATIO
    );
    const uPlotDecimationCap = Math.floor(
      (width * MAX_RENDERED_POINTS_PER_WIDTH) / MIN_MAX_LAST_POINTS_PER_BIN
    );
    return Math.floor(
      Math.min(minMaxLastBinTarget, uPlotDecimationCap) * factor
    );
  }

  if (primaryLineType === 'full-fidelity' || primaryLineType === undefined) {
    return defaultBinCount * factor;
  }

  // Exhaustiveness: if a new PrimaryLineType is added, this assignment fails
  // at compile time, forcing an explicit bin-sizing decision for the new case.
  const _exhaustive: never = primaryLineType;
  void _exhaustive;
  return defaultBinCount * factor;
}

export function usePanelInteractionContext() {
  const context = useContext(PanelInteractionContext);

  if (!context) {
    console.error(
      'usePanelInteractionContext must be used within a PanelInteractionProvider'
    );

    return {
      fullScreenNumBuckets: FULL_SCREEN_MAX_BINS,
      numBucketsByPanelWidth: 1000,
      setZooming: () => {},
      sizeRef: null,
      zooming: false,
    };
  }

  return context;
}
